import { yupResolver } from '@hookform/resolvers/yup';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
    Container,
    ButtonsContainer,
    ReportModalSubHeading,
    ReportModalButton,
    ReportModalError,
} from './styled';

import { ButtonVariant, ButtonSize } from '../../../Button';
import { FormInput } from '../../../FormInput';
import { SuccessModal } from '../SuccessModal';

interface IReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (props: IReportOnSubmitProps) => Promise<unknown>;
}

export interface IReportOnSubmitProps {
    title: string;
    description: string;
}

const VALIDATION_SCHEMA = yup.object({
    title: yup.string().required(`Title name can't be blank.`),
    description: yup
        .string()
        .required(`Description name can't be blank.`)
        .max(255, `Description must be less than 255 characters.`),
});

export const ReportModal: FC<IReportModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
}) => {
    const [isSuccess, setSuccess] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [networkError, setNetworkError] = useState('');
    const formMethods = useForm<
        Record<keyof typeof VALIDATION_SCHEMA.fields, string>
    >({
        resolver: yupResolver(VALIDATION_SCHEMA),
        mode: 'onTouched',
        defaultValues: {
            title: '',
            description: '',
        },
    });

    const { handleSubmit, getValues, reset } = formMethods;

    const onSubmitHandler = useCallback(async () => {
        try {
            setLoading(true);
            await onSubmit(getValues());
            setSuccess(true);
        } catch (err) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setNetworkError((err as any)?.message);
        } finally {
            setLoading(false);
        }
    }, [onSubmit, getValues, setNetworkError, setSuccess]);

    const onCloseHandler = useCallback(() => {
        setSuccess(false);
        setNetworkError('');
        reset();
        onClose();
    }, [onClose, reset]);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
                <SuccessModal
                    isSuccess={isSuccess}
                    isOpen={isOpen}
                    onClose={onCloseHandler}
                    successTitle="Issue Submitted"
                    successText="Thank you for reporting this issue"
                    title="Report an issue"
                    footer={
                        <ButtonsContainer>
                            <ReportModalButton
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                type="button"
                                onClick={onCloseHandler}
                            >
                                Cancel
                            </ReportModalButton>
                            <ReportModalButton
                                size={ButtonSize.SMALL}
                                variant={ButtonVariant.PRIMARY}
                                disabled={isLoading}
                                type="submit"
                            >
                                Report
                            </ReportModalButton>
                        </ButtonsContainer>
                    }
                >
                    <Container>
                        <ReportModalSubHeading>
                            Tell us what went wrong
                        </ReportModalSubHeading>
                        {networkError && (
                            <ReportModalError>
                                Error occured, please try again later.
                            </ReportModalError>
                        )}
                        <FormInput id="title" label="Title" />
                        <FormInput
                            id="description"
                            label="Description"
                            type="textarea"
                        />
                    </Container>
                </SuccessModal>
            </form>
        </FormProvider>
    );
};
